//helpers
import NFA from 'shared/helpers/analytics/NFAnalytics';
import { getDynamicCategoryLevels } from 'shared/layouts/DetailsPage/components/ProductInfo/helpers';
import { EVENT_NAMES, EVENT_TYPES } from 'shared/helpers/utils/impressions';
import { getMergedDataWithRetinaData } from 'shared/helpers/analytics/helpers';

//constants
import { WISHLIST_EVENTS } from 'shared/helpers/analytics/constants/wishlist';
import { TrackingMetaData } from 'shared/components/Wishlist/types';
import { transformTrackingForWishlist } from 'shared/components/Wishlist/helper';
interface TrackNotifyMeParams {
  productId: string;
  price: string | number;
  isOutOfStock: boolean;
  productName: string;
  productBrand: string;
  productSKU: string;
  discountedPrice: string;
  discount: number;
  tags?: { [key: string]: any };
  type?: string;
  categoryLevels?: {
    [key: string]: { id: string; name: string };
  };
  variantType: string;
  variantProductID: string;
  productBadge?: string;
  productData: Record<string, any>;
  email?: string;
}

const getTagString = (tags: { [key: string]: any } = []) => {
  if (tags.length >= 0) {
    return tags.map((tag) => tag.title).join(',');
  }

  return '';
};

const getWishlistTrackData = (trackingData: TrackingMetaData) => {
  const categoryLevels = getDynamicCategoryLevels(trackingData?.categoryLevels);
  const productTags = getTagString(trackingData.productTags);

  return {
    wishlistProductType: trackingData.productType,
    wishlistProductCategoryName: trackingData.categoryName,
    wishlistProductDiscountPercent: trackingData.discountPercent,
    wishlistProductBadge: trackingData.productBadge || undefined,
    wishlistProductMRP: trackingData.MRP,
    wishlistProductName: trackingData.productName,
    wishlistProductSKU: trackingData.productSKU,
    wishlistProductDiscountedPrice: trackingData.discountedPrice,
    wishlistProductBrandName: trackingData.productBrandName,
    wishlistProductIsOutOfStock: trackingData.isOutOfStock,
    wishlistProductID: trackingData.productID,
    wishlistProductCategoryLevel1: categoryLevels?.categoryLevel1 || undefined,
    wishlistProductCategoryLevel2: categoryLevels?.categoryLevel2 || undefined,
    wishlistProductCategoryLevel3: categoryLevels?.categoryLevel3 || undefined,
    wishlistProductCategoryLevel4: categoryLevels?.categoryLevel4 || undefined,
    wishlistProductTags: productTags,
    wishlistIsProductOffersAvailable: trackingData?.isProductOffersAvailable,
    wishlistIsPriceRevealProduct: trackingData.isPriceReveal,
    wishlistIsFomoTimerShown: trackingData?.isFomoTimerVisible,
    wishlistIsSalePriceLive: trackingData?.isSalePriceLive,
    wishlistSpecialTagEnabled: trackingData?.specialTagEnabled,
    wishlistSpecialTag: trackingData?.specialTag || undefined,
    wishlistSpecialTagType: trackingData?.specialTagType || undefined,
    wishlistIsPriceParticipatingSku: trackingData?.isPriceParticipatingSku,
    wishlistPriceParticipatingSkuType: trackingData?.priceParticipatingSkuType || undefined,
  };
};

export const trackAddToWishlistClicked = (trackingData: TrackingMetaData) => {
  const { impressionsData = {} } = trackingData;

  NFA.track({
    event: WISHLIST_EVENTS.ADD_TO_WISHLIST,
    data: getMergedDataWithRetinaData(
      transformTrackingForWishlist(impressionsData, false),
      getWishlistTrackData(trackingData)
    ),
  });
};

export const trackRemoveFromWishlistClicked = (trackingData: TrackingMetaData) => {
  const { impressionsData = {} } = trackingData;

  NFA.track({
    event: WISHLIST_EVENTS.REMOVE_FROM_WISHLIST,
    data: getMergedDataWithRetinaData(
      transformTrackingForWishlist(impressionsData, true),
      getWishlistTrackData(trackingData)
    ),
  });
};

export const trackNotifyMeClicked = ({
  productId,
  price,
  isOutOfStock,
  discountedPrice,
  discount,
  type,
  categoryLevels,
  variantType,
  variantProductID,
  productBadge,
  productData,
  email,
}: TrackNotifyMeParams) => {
  const {
    brandName = '',
    discountedPrice: dp = '',
    id = '',
    price: p = '',
    tag_list = [],
  } = productData || {};

  const retinaData = {
    product_brand: brandName,
    product_dp: dp,
    product_id: id,
    product_mrp: p,
    product_price: dp,
    product_tags: tag_list ? tag_list.map((item: { title: string }) => item.title) : undefined,
    event_name: EVENT_NAMES.NOTIFY_ME,
    event_type: EVENT_TYPES.CLICK,
  };

  const transformedCategoryLevels = getDynamicCategoryLevels(categoryLevels);

  const data = {
    productID: productId,
    MRP: price,
    isOutOfStock: isOutOfStock,
    discountedPrice,
    discountPercent: discount,
    ...(type && { productType: type }),
    categoryLevel1: transformedCategoryLevels?.categoryLevel1 || undefined,
    categoryLevel2: transformedCategoryLevels?.categoryLevel2 || undefined,
    categoryLevel3: transformedCategoryLevels?.categoryLevel3 || undefined,
    categoryLevel4: transformedCategoryLevels?.categoryLevel4 || undefined,
    variantType,
    variantProductID,
    productBadge,
    notifyMeEmail: email,
  };

  NFA.track({
    event: WISHLIST_EVENTS.NOTIFY_ME_CLICKED,
    data: getMergedDataWithRetinaData(retinaData, data),
  });
};

interface TrackWishlistFilterParams {
  selectedFilter: string;
  wishlistProductCount: number;
  isLoggedIn: boolean;
  customerId?: string;
  mobileDeviceId?: string;
}

export const trackWishlistFilterSelected = ({
  selectedFilter,
  wishlistProductCount,
  isLoggedIn,
  customerId,
  mobileDeviceId,
}: TrackWishlistFilterParams) => {
  const data = {
    selected_filter: selectedFilter,
    wishlist_product_count: wishlistProductCount,
    is_logged_in: isLoggedIn.toString(),
    ...(customerId && { nykaa_customer_id: customerId }),
    ...(mobileDeviceId && { mobile_device_id: mobileDeviceId }),
    source_platform: 'web', // Since this is web application
  };

  NFA.track({
    event: WISHLIST_EVENTS.FILTER_SELECTED,
    data,
  });
};
